import { useEffect, useRef, useState } from 'react';
import { Check, Minus, Plus, User as UserIcon } from 'lucide-react';
import type { DtpProductResponse } from '@bloom/shared';
import { PRESETS, FREQ_LABELS, FREQ_PER_WEEK, type PeptidePreset, type TierKey, type FrequencyKey } from '../../data/peptidePresets';
import { presetKeyForFamilySlug } from '../../data/peptidePresetSlugMap';
import { calcCompound } from '../../utils/peptideCalculations';
import { usePatientProfile, type PatientProfile } from '../../hooks/usePatientProfile';
import { getFamilyDisplayName } from './educational/useEducationalCatalog';
import { wouldExceedWeightLossCap, WEIGHT_LOSS_CAP_MESSAGE } from '../../utils/membershipConstraints';

/**
 * Patient-facing protocol builder. Surfaces clinician-grade dose math from the
 * admin peptide calculator (PRESETS + calcCompound), but presents it in plain
 * language: "0.25 mg once a week" instead of "5 units, 0.05 mL, vial lasts 5 wk".
 *
 * Two display modes:
 *   - Single product (educational store family page): shows one card with tier
 *     control. No selection toggle, no parent-driven cap.
 *   - Multi product (membership flow): N candidates with selection toggles.
 *     `requiredCount` enforces the cap; `onPicksChange` reports current ids +
 *     dose notes back to the parent.
 *
 * Patients only see three tier levels (Light / Standard / Strong) — micro and
 * veryhigh are hidden because they are clinical-trial / sub-therapeutic / >FDA-
 * max protocols not appropriate to suggest without a clinician.
 */

type PatientTier = 'light' | 'standard' | 'strong';
const PATIENT_TIER_TO_PRESET: Record<PatientTier, TierKey> = {
  light: 'low',
  standard: 'moderate',
  strong: 'high'
};
const PATIENT_TIER_LABEL: Record<PatientTier, string> = {
  light: 'Light',
  standard: 'Standard',
  strong: 'Strong'
};

export interface PatientProtocolPick {
  productId: string;
  drugFamilySlug: string;
  tier: PatientTier;
  doseNote: string; // human-readable, e.g. "0.25 mg once a week"
}

interface Props {
  candidates: DtpProductResponse[];
  /** Membership: how many slots the patient must fill. Omit for educational. */
  requiredCount?: number;
  /** Membership: pre-selected products (when re-suggesting). */
  initialSelectedIds?: string[];
  /** Membership: notify the parent as the patient adjusts. */
  onPicksChange?: (picks: PatientProtocolPick[]) => void;
  /** Used to look up family display names. */
  allProducts?: DtpProductResponse[];
}

export function PatientProtocolBuilder({
  candidates,
  requiredCount,
  initialSelectedIds,
  onPicksChange,
  allProducts
}: Props) {
  const { profile, update, persist, isAuthenticated } = usePatientProfile();

  // Per-product tier state. Defaults to 'standard'.
  const [tiers, setTiers] = useState<Record<string, PatientTier>>({});
  // Membership-only: which products are included. For educational (no
  // requiredCount), all candidates are "included" and the toggle is hidden.
  const [includedIds, setIncludedIds] = useState<Set<string>>(() =>
    new Set(initialSelectedIds || (requiredCount ? [] : candidates.map(c => c._id)))
  );

  // Notify parent when picks change. Hold the callback in a ref so a parent
  // that passes an inline arrow function doesn't retrigger the effect every
  // render (which previously caused an infinite update loop on the membership
  // page where DtpMembership re-creates onPicksChange on each render).
  const onPicksChangeRef = useRef(onPicksChange);
  useEffect(() => {
    onPicksChangeRef.current = onPicksChange;
  }, [onPicksChange]);

  useEffect(() => {
    const cb = onPicksChangeRef.current;
    if (!cb) return;
    const picks: PatientProtocolPick[] = [];
    for (const p of candidates) {
      if (!includedIds.has(p._id)) continue;
      const tier = tiers[p._id] || 'standard';
      const recommendation = computeRecommendation(p, profile, tier);
      if (!recommendation) continue;
      picks.push({
        productId: p._id,
        drugFamilySlug: p.drugFamilySlug || '',
        tier,
        doseNote: recommendation.label
      });
    }
    cb(picks);
  }, [includedIds, tiers, candidates, profile]);

  const requireToggle = !!requiredCount;
  const overLimit = !!requiredCount && includedIds.size > requiredCount;
  const includedCount = includedIds.size;

  function setTier(productId: string, tier: PatientTier) {
    setTiers(prev => ({ ...prev, [productId]: tier }));
  }
  function toggleInclude(productId: string) {
    setIncludedIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        // Enforce cap: do not add if already at requiredCount
        if (requiredCount && next.size >= requiredCount) return prev;
        // Enforce single weight-loss peptide per membership
        if (wouldExceedWeightLossCap(productId, Array.from(prev), candidates)) return prev;
        next.add(productId);
      }
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <ProfileEditor
        profile={profile}
        update={update}
        persist={persist}
        isAuthenticated={isAuthenticated}
      />

      {requireToggle && (
        <div className="text-xs text-[#8a8175] flex items-center gap-2">
          <span>{includedCount} of {requiredCount} slots filled</span>
          {overLimit && <span className="text-red-700">— too many selected</span>}
        </div>
      )}

      <div className="space-y-3">
        {candidates.map(p => {
          const tier = tiers[p._id] || 'standard';
          const rec = computeRecommendation(p, profile, tier);
          const familyName = p.drugFamilySlug && allProducts
            ? getFamilyDisplayName(p.drugFamilySlug, allProducts)
            : p.drugName;
          const included = includedIds.has(p._id);
          const blockedByWeightLossCap =
            requireToggle && !included &&
            wouldExceedWeightLossCap(p._id, Array.from(includedIds), candidates);

          return (
            <div
              key={p._id}
              className={`rounded-2xl border p-4 md:p-5 transition ${
                included
                  ? 'border-[#2d4a3a] bg-[#eaf2ec]/40'
                  : 'border-[#e8e2d6] bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-[16px] text-[#1a1612]">{familyName}</h3>
                    {rec && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-white border border-[#e8e2d6] text-[#4a4238]">
                        {PATIENT_TIER_LABEL[tier]}
                      </span>
                    )}
                  </div>
                  {rec ? (
                    <div className="mt-1.5 text-[15px] text-[#1a1612]">
                      <span className="font-semibold">{rec.label}</span>
                    </div>
                  ) : (
                    <div className="mt-1.5 text-sm text-[#8a8175]">
                      Dosing handled with your physician at approval.
                    </div>
                  )}
                  {rec?.subnote && (
                    <div className="mt-1 text-xs text-[#8a8175]">{rec.subnote}</div>
                  )}
                </div>
                {requireToggle && (
                  <button
                    onClick={() => toggleInclude(p._id)}
                    aria-pressed={included}
                    disabled={blockedByWeightLossCap}
                    title={blockedByWeightLossCap ? WEIGHT_LOSS_CAP_MESSAGE : undefined}
                    className={`flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border transition ${
                      included
                        ? 'bg-[#2d4a3a] border-[#2d4a3a] text-white'
                        : 'bg-white border-[#c9beac] text-[#c9beac]'
                    } ${blockedByWeightLossCap ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    {included ? <Check className="w-4 h-4" strokeWidth={3} /> : <Plus className="w-4 h-4" />}
                  </button>
                )}
              </div>

              {rec && (
                <div className="mt-4 inline-flex rounded-full border border-[#e8e2d6] bg-white p-0.5">
                  {(['light', 'standard', 'strong'] as PatientTier[]).map(t => (
                    <button
                      key={t}
                      onClick={() => setTier(p._id, t)}
                      className={`px-3 py-1 text-xs rounded-full transition ${
                        tier === t
                          ? 'bg-[#2d4a3a] text-white'
                          : 'text-[#4a4238] hover:bg-[#faf8f4]'
                      }`}
                    >
                      {PATIENT_TIER_LABEL[t]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-[11px] text-[#8a8175] leading-relaxed">
        Suggestions only — your physician reviews and approves the final protocol before any medication ships.
      </div>
    </div>
  );
}

interface Recommendation {
  label: string;       // "0.25 mg once a week"
  subnote?: string;    // tier info or weight-based hint
}

function computeRecommendation(
  product: DtpProductResponse,
  profile: PatientProfile,
  patientTier: PatientTier
): Recommendation | null {
  const presetKey = presetKeyForFamilySlug(product.drugFamilySlug);
  if (!presetKey) return null;
  const preset: PeptidePreset = PRESETS[presetKey];
  if (!preset) return null;

  const presetTierKey = PATIENT_TIER_TO_PRESET[patientTier];
  const tier = preset.tiers[presetTierKey];
  if (!tier) return null;

  // Re-run the calc engine for fidelity (we don't display volume to patients
  // but we want the math path to match the admin tool).
  calcCompound({
    dose: tier.dose,
    doseUnit: tier.doseUnit,
    conc: preset.conc,
    concUnit: preset.concUnit,
    vial: preset.vial,
    freq: tier.freq
  });

  const doseLabel = `${formatDose(tier.dose, tier.doseUnit)} ${friendlyFreq(tier.freq)}`;

  const weeklyMg = (tier.doseUnit === 'mcg' ? tier.dose / 1000 : tier.dose) * (FREQ_PER_WEEK[tier.freq] || 1);
  let subnote: string | undefined;
  if (preset.weightBased) {
    const weightKg = profile.weightUnit === 'kg' ? profile.weight : profile.weight / 2.20462;
    subnote = `${formatPerKg(weeklyMg, weightKg, tier.doseUnit)} per kg per week — adjusts to your weight.`;
  }

  return { label: doseLabel, subnote };
}

function formatDose(value: number, unit: 'mg' | 'mcg' | 'units'): string {
  if (unit === 'mcg') return `${value} mcg`;
  if (unit === 'units') return `${value} units`;
  // mg — strip trailing zeros
  return `${value % 1 === 0 ? value : value.toString()} mg`;
}

function friendlyFreq(freq: FrequencyKey): string {
  switch (freq) {
    case 'daily': return 'every day';
    case '5on2off': return '5 days a week';
    case '3x_week': return '3 times a week';
    case '2x_week': return '2 times a week';
    case '1x_week': return 'once a week';
    case 'biweekly': return 'every 2 weeks';
    case 'monthly': return 'monthly';
    default: return FREQ_LABELS[freq];
  }
}

function formatPerKg(weeklyMg: number, weightKg: number, originalUnit: 'mg' | 'mcg' | 'units'): string {
  if (weightKg <= 0) return '';
  const perKg = weeklyMg / weightKg;
  if (originalUnit === 'mcg' || perKg < 0.01) {
    return `~${(perKg * 1000).toFixed(0)} mcg`;
  }
  return `~${perKg.toFixed(2)} mg`;
}

function ProfileEditor({
  profile,
  update,
  persist,
  isAuthenticated
}: {
  profile: PatientProfile;
  update: (p: Partial<PatientProfile>) => void;
  persist: () => Promise<void>;
  isAuthenticated: boolean;
}) {
  const [open, setOpen] = useState(false);

  const summary = `${profile.sex === 'female' ? 'F' : 'M'} · ${profile.age}y · ${profile.weight} ${profile.weightUnit} · ${profile.heightUnit === 'in' ? `${Math.floor(profile.height / 12)}'${profile.height % 12}"` : `${profile.height} cm`}`;

  return (
    <div className="rounded-2xl border border-[#e8e2d6] bg-white">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#eaf2ec] text-[#2d4a3a] flex items-center justify-center">
            <UserIcon className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#8a8175]">Your profile</div>
            <div className="text-sm text-[#1a1612]">{summary}</div>
          </div>
        </div>
        <span className="text-xs text-[#2d4a3a] underline">{open ? 'Done' : 'Edit'}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-[#e8e2d6] pt-4">
          <NumberField
            label="Age"
            value={profile.age}
            onChange={v => update({ age: v })}
            min={18}
            max={99}
          />
          <SegField
            label="Sex"
            value={profile.sex}
            options={[{ value: 'male', label: 'M' }, { value: 'female', label: 'F' }]}
            onChange={(v) => update({ sex: v as 'male' | 'female' })}
          />
          <NumberField
            label={`Weight (${profile.weightUnit})`}
            value={profile.weight}
            onChange={v => update({ weight: v })}
            min={50}
            max={600}
          />
          <NumberField
            label={`Height (${profile.heightUnit})`}
            value={profile.height}
            onChange={v => update({ height: v })}
            min={profile.heightUnit === 'in' ? 48 : 120}
            max={profile.heightUnit === 'in' ? 84 : 220}
          />
          <div className="col-span-2 md:col-span-4 flex justify-end pt-1">
            {isAuthenticated && (
              <button
                onClick={() => { persist(); setOpen(false); }}
                className="text-xs text-[#2d4a3a] underline"
              >
                Save to my profile
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NumberField({
  label, value, onChange, min, max
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
}) {
  // `draft` holds what has actually been typed, until the field is left.
  //
  // This used to clamp inside the input's own onChange against a controlled
  // `value`, which rewrote the box mid-number: with Weight's min of 50, typing
  // "215" went 2 → clamped 50 → 501 → clamped 600, so a patient could not enter
  // their own weight. Clearing the field was impossible for the same reason,
  // since Number('') is 0 rather than NaN and clamped straight back up to the
  // minimum. The bounds are real, they just belong at the end of the number
  // rather than on every character of it.
  const [draft, setDraft] = useState<string | null>(null);

  const commit = () => {
    if (draft === null) return;
    const n = Number(draft);
    // An empty or unparseable box means "I did not finish" — keep the value
    // that was there rather than inventing the minimum.
    const next = draft.trim() === '' || Number.isNaN(n)
      ? value
      : Math.max(min, Math.min(max, n));
    setDraft(null);
    if (next !== value) onChange(next);
  };

  const step = (delta: number) => {
    setDraft(null);
    onChange(Math.max(min, Math.min(max, value + delta)));
  };

  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.15em] text-[#8a8175] mb-1">{label}</label>
      <div className="flex items-center gap-2 border border-[#e8e2d6] rounded-lg px-2 py-1.5">
        <button
          onClick={() => step(-1)}
          className="w-6 h-6 flex items-center justify-center text-[#8a8175] hover:text-[#1a1612]"
        >
          <Minus className="w-3 h-3" />
        </button>
        <input
          type="number"
          inputMode="numeric"
          value={draft ?? String(value)}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          className="flex-1 text-center text-sm bg-transparent outline-none w-full"
        />
        <button
          onClick={() => step(1)}
          className="w-6 h-6 flex items-center justify-center text-[#8a8175] hover:text-[#1a1612]"
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function SegField({
  label, value, options, onChange
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.15em] text-[#8a8175] mb-1">{label}</label>
      <div className="inline-flex border border-[#e8e2d6] rounded-lg p-0.5 w-full">
        {options.map(o => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={`flex-1 px-2 py-1 text-sm rounded ${
              value === o.value
                ? 'bg-[#2d4a3a] text-white'
                : 'text-[#4a4238]'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
